import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDepartments } from '../../../hooks/useDepartments';
import Modal from '../../ui/Modal';
import Input from '../../ui/Input';
import Select from '../../ui/Select';
import Button from '../../ui/Button';
import { 
  SECTORS, 
  USER_ROLES,
  getSectorLabel
} from '../../../constants/authConstants';
import { validateProjectForm } from '../../../utils/projectValidation';
import { PROJECT_STATUS } from '../../../constants/projectConstants';

const ProjectEditModal = ({ 
  isOpen, 
  onClose, 
  project, 
  onUpdate,
  loading = false,
  isLocked = false
}) => {
  const { user, getUserRole } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const { getDepartmentLabel, getDepartmentOptions } = useDepartments();
  
  const isAdmin = getUserRole() === USER_ROLES.ADMIN;
  
  const [formData, setFormData] = useState({
    sector: '',
    department: '',
    projectName: '',
    financialRequirement: '',
    brief: '',
    priority: ''
  });
  
  const [errors, setErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const gridCols2 = getResponsiveValue({
    mobile: 'grid-cols-1',
    desktop: 'grid-cols-2'
  });

  const buttonSize = getResponsiveValue({
    mobile: 'w-full',
    desktop: 'w-auto'
  });

  useEffect(() => {
    if (isOpen && project) {
      if (isLocked && !isAdmin) {
        setErrors({ 
          general: 'Your submission is locked. You cannot edit this project. Please contact an administrator to unlock.' 
        });
      }
      
      setFormData({
        sector: project.sector || '',
        department: project.department || '',
        projectName: project.projectName || '',
        financialRequirement: project.financialRequirement?.toString() || '',
        brief: project.brief || '',
        priority: project.priority?.toString() || ''
      });
      
      if (!isLocked || isAdmin) {
        setErrors({});
      }
    }
  }, [isOpen, project, isLocked, isAdmin]);

  const handleInputChange = (field, value) => {
    if (isLocked && !isAdmin) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const validation = validateProjectForm(formData, isAdmin);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleUpdate = async () => {
    if (isLocked && !isAdmin) {
      setErrors({ 
        general: 'Your submission is locked. You cannot update this project. Please contact an administrator to unlock.' 
      });
      return;
    }
    
    if (!validateForm()) return;
    
    try {
      setActionLoading(true);
      await onUpdate(project.id, formData);
      handleClose();
    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => {
    if (!actionLoading) {
      setFormData({
        sector: '',
        department: '',
        projectName: '',
        financialRequirement: '',
        brief: '',
        priority: ''
      });
      setErrors({});
      onClose();
    }
  };

  const getAvailableDepartments = () => {
    if (isAdmin) {
      if (formData.sector) {
        return getDepartmentOptions(formData.sector);
      }
      return [];
    } else {
      return [{
        value: formData.department,
        label: getDepartmentLabel(formData.department)
      }];
    }
  };

  const sectorOptions = SECTORS.map(sector => ({
    value: sector.value,
    label: sector.label
  }));

  if (!project || project.status !== PROJECT_STATUS.PENDING) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Project Proposal"
      size="xl"
      maxHeight="large"
    >
      <div className={containerPadding}>
        <div className="space-y-6">
          {isLocked && !isAdmin && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-orange-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="text-orange-800 text-sm font-semibold">Submission Locked</span>
                  <p className="text-orange-600 text-xs mt-0.5">
                    You cannot edit this project.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {errors.general && (
            <div className="bg-error-subtle border border-border-error rounded-xl p-3">
              <div className="flex items-center">
                <svg className="w-4 h-4 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-error text-sm">{errors.general}</span>
              </div>
            </div>
          )}

          <div className="pb-4 border-b border-border-primary">
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              Edit Project Details
            </h3>
            <p className="text-sm text-text-secondary">
              Update the project information. Changes will be saved immediately.
            </p>
          </div>

          <div className={`grid ${gridCols2} gap-6`}>
            <div>
              {isAdmin ? (
                <Select
                  label="Sector"
                  value={formData.sector}
                  onChange={(e) => handleInputChange('sector', e.target.value)}
                  options={sectorOptions}
                  error={errors.sector}
                  required
                  placeholder="Select sector"
                  disabled={actionLoading || (isLocked && !isAdmin)}
                />
              ) : (
                <div>
                  <label className="text-sm font-medium text-text-primary mb-1 block">
                    Sector <span className="text-error">*</span>
                  </label>
                  <div className={`px-3 py-2 bg-surface-secondary rounded-xl border border-border-primary text-text-primary text-sm ${isLocked ? 'opacity-60' : ''}`}>
                    {getSectorLabel(formData.sector) || 'Not assigned'}
                  </div>
                </div>
              )}
            </div>

            <div>
              {isAdmin ? (
                <Select
                  label="Department"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  options={getAvailableDepartments()}
                  error={errors.department}
                  required
                  placeholder="Select department"
                  disabled={!formData.sector || actionLoading || (isLocked && !isAdmin)}
                />
              ) : (
                <div>
                  <label className="text-sm font-medium text-text-primary mb-1 block">
                    Department <span className="text-error">*</span>
                  </label>
                  <div className={`px-3 py-2 bg-surface-secondary rounded-xl border border-border-primary text-text-primary text-sm ${isLocked ? 'opacity-60' : ''}`}>
                    {getDepartmentLabel(formData.department) || 'Not assigned'}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={`grid ${gridCols2} gap-6`}>
            <Input
              label="Name of Project"
              type="text"
              value={formData.projectName}
              onChange={(e) => handleInputChange('projectName', e.target.value)}
              placeholder="Enter project name"
              error={errors.projectName}
              required
              disabled={actionLoading || (isLocked && !isAdmin)}
            />

            <Input
              label="Total Estimate Cost (in lakh)"
              type="number"
              value={formData.financialRequirement}
              onChange={(e) => handleInputChange('financialRequirement', e.target.value)}
              placeholder="Enter amount in lakhs"
              error={errors.financialRequirement}
              required
              disabled={actionLoading || (isLocked && !isAdmin)}
              icon={<span className="text-gray-400 font-semibold">₹</span>}
            />
          </div>

          <div className={`grid ${gridCols2} gap-6`}>
            <Input
              label="Priority (Optional)"
              type="number"
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', e.target.value)}
              placeholder="Enter priority (1 = highest)"
              error={errors.priority}
              disabled={actionLoading || (isLocked && !isAdmin)}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
              }
            />
          </div>

          <div>
            <label className="text-sm font-medium text-text-primary mb-1 block">
              Brief Description <span className="text-error">*</span>
            </label>
            <textarea
              value={formData.brief}
              onChange={(e) => handleInputChange('brief', e.target.value)}
              placeholder="Provide a detailed description of the project..."
              rows={8}
              disabled={actionLoading || (isLocked && !isAdmin)}
              className={`
                w-full border border-border-primary rounded-xl bg-surface-primary
                focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-border-focus
                text-text-primary placeholder:text-text-tertiary
                transition-colors duration-200 px-3 py-2 text-sm
                ${errors.brief ? 'border-border-error focus:ring-border-error focus:border-border-error' : ''}
                ${(actionLoading || (isLocked && !isAdmin)) ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            />
            {errors.brief && (
              <span className="text-sm text-error flex items-center mt-1">
                <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.brief}
              </span>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-border-primary justify-end">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={actionLoading}
              className={buttonSize}
            >
              Cancel
            </Button>
            
            <Button
              variant="primary"
              onClick={handleUpdate}
              disabled={actionLoading || (isLocked && !isAdmin)}
              loading={actionLoading}
              className={buttonSize}
            >
              {actionLoading ? 'Updating...' : 'Update Project'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProjectEditModal;